import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ILoadedInventory } from 'src/app/inventory/interfaces/ILoadedInventory';
import { IFileUploadObject } from 'src/app/shared/components/file-uploader/file-upload-object.interface';
import { CSV_MIME_TYPE, PLAIN_TEXT_MIME_TYPE, XLSX_MIME_TYPE, XLS_MIME_TYPE } from 'src/app/shared/constants/constants';
import { FileUploaderErrorsEnum } from 'src/app/shared/interfaces/FileUploaderErrorsEnum';
import { IDrugShort } from 'src/app/shared/interfaces/IDrugShort.interface';
import { DrugsService } from 'src/app/shared/services/drugs.service';
import { ExcelReader } from 'src/app/shared/services/excel-reader.service';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';

@UntilDestroy()
@Component({
  selector: 'app-load-inventory-file-uploader-dialog',
  templateUrl: './load-inventory-file-uploader-dialog.component.html',
  styleUrls: ['./load-inventory-file-uploader-dialog.component.scss']
})
export class LoadInventoryFileUploaderDialogComponent  implements OnInit {

  @Output() inventoryLoaded: EventEmitter<ILoadedInventory[]> = new EventEmitter();
  @Output() closeDialog: EventEmitter<void> = new EventEmitter();

  @Select(InventoryState.loading) loading$: Observable<boolean | undefined>;

  parsedFile: string[][];

  fileUploaderError: FileUploaderErrorsEnum = FileUploaderErrorsEnum.None;

  headers: string[];

  form: UntypedFormGroup;

  constructor(protected store: Store, private excelReader: ExcelReader, private drugService: DrugsService, private fb: UntypedFormBuilder) { 
   this.form = fb.group({
    ndcHeaderValidity: ['', Validators.required],
    qtyHeaderValidity: ['', Validators.required],
    ndcHeaderIndex: [-1, Validators.required],
    qtyHeaderIndex: [-1, Validators.required]
   });
  }

  ngOnInit(): void {
    this.drugService?.fetchDrugs()
    ?.pipe(take(1))
    ?.subscribe();
  }

  addAttachmentFile(file): void {
    this.parsedFile = file;
  }

  async afterFileSelected(file: IFileUploadObject): Promise<void> {
    if (file) {
      this.fileUploaderError = FileUploaderErrorsEnum.None;
      const fileToParse = file.selectedFile;
      const acceptTypes = [PLAIN_TEXT_MIME_TYPE, CSV_MIME_TYPE, XLS_MIME_TYPE, XLSX_MIME_TYPE];
      if (acceptTypes.includes(fileToParse.type)) {
        const excelData = await this.excelReader.readExcelFile(fileToParse);
        const headers = this.excelReader.getExcelHeaders(excelData);
        if (!headers?.length || headers.length < 2) {
          this.fileUploaderError = FileUploaderErrorsEnum.InvalidFile;
          return;
        }
        this.headers = headers;
        this.parsedFile = this.excelReader.getRows(excelData);
      }
      else {
         this.fileUploaderError = FileUploaderErrorsEnum.InvalidFormat;
      }
    }
  }

  loadInventory(): void {
    const rows = this.parsedFile;
    const ndcIndex = this.form.get('ndcHeaderIndex').value;
    const qtyIndex = this.form.get('qtyHeaderIndex').value;
    if (this.form.valid && ndcIndex > -1 && qtyIndex > -1) {
      const result: ILoadedInventory[] = [];
      rows.forEach(row => {
        let ndc = row[ndcIndex];
        let qty = +row[qtyIndex];
        if (!isNaN(qty) && qty > 0) {
          const drug = this.drugService.getDrugByNdc(ndc);
          if (drug) {
            result.push({id: drug.id, name: drug.friendlyName, packageNdc: drug.packageNdc, adding: qty, schedule: drug.schedule, gpi: (<IDrugShort>drug).gpi})
          }
        } 
      });
      this.inventoryLoaded.next(result);
    }
  }

  getHeaderText(): string {
    if (this.parsedFile) {
      return 'Select Columns';
    }
    return 'Load from File';
  }

  getSubHeaderText(): string {
    if (this.parsedFile) {
      return 'Please select the columns in the file that represent NDC and Quantity';
    }
    return 'You can attach TXT, CSV, or Excel format only';
  }

  ndcHeaderSelected(ndcHeader: string): void {
   const index = this.headers.indexOf(ndcHeader);
    this.form.controls.ndcHeaderValidity.setErrors(null);
   if (index > -1) {
      if (this.parsedFile?.some(row => this.isValidNdcRow(row[index]))) {
        this.form.controls.ndcHeaderIndex.setValue(index);
      } else {
        this.form.controls.ndcHeaderValidity.setErrors({custom: 'Invalid NDC column'});
        this.form.controls.ndcHeaderValidity.markAsTouched(); 
      }
   
   } else {
    this.form.controls.ndcHeaderValidity.setErrors({custom: 'Cannot parse index'});
    this.form.controls.ndcHeaderValidity.markAsTouched(); 
   }
  }

  countHeaderSelected(countHeader: string): void {
    const index = this.headers.indexOf(countHeader);
    this.form.controls.qtyHeaderValidity.setErrors(null);
   if (index > -1) {
      if (this.parsedFile?.some(row => !isNaN(+row[index]))) {
        this.form.controls.qtyHeaderIndex.setValue(index);
      } else {
        this.form.controls.qtyHeaderValidity.setErrors({custom: 'Invalid Quantity column'});
        this.form.controls.qtyHeaderValidity.markAsTouched(); 
      }
   
   } else {
    this.form.controls.qtyHeaderValidity.setErrors({custom: 'Cannot parse index'});
    this.form.controls.qtyHeaderValidity.markAsTouched(); 
   }
  }

  uploadAnotherFile(): void {
    this.fileUploaderError = FileUploaderErrorsEnum.None;
    this.parsedFile = null;
    this.form.reset();
  }

  isValidNdcRow(content: string): boolean {
    const ndc = content?.toString()?.replace(/-/g, '');
    if (ndc?.length === 10 || ndc?.length === 11) {
      return !isNaN(+ndc);
    }
    return false;
  }

  close(): void {
    this.closeDialog.emit();
  }


}
